import PageGlow from "../components/PageGlow";
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Globe, Star, Users, Building2, BadgeCheck, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, SITE_URL, fileUrl } from "../lib/api";
import ImageLightbox from "../components/ImageLightbox";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import JobCard from "../components/JobCard";
import Reveal from "../components/Reveal";
import ShareButtons from "../components/ShareButtons";

function Stars({ value, size = "h-4 w-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={`star-${i}`} className={`${size} ${i <= Math.round(value) ? "fill-yellow-400 text-yellow-400" : "text-zinc-300"}`} />
      ))}
    </span>
  );
}

export default function CompanyProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [logoErr, setLogoErr] = useState(false);
  const [photoViewer, setPhotoViewer] = useState(null);

  const load = () => {
    api.get(`/companies/${slug}`).then((res) => {
      const canon = res.data?.company?.canonical_slug;
      if (canon && canon !== slug) {
        navigate(`/companies/${canon}`, { replace: true });
        return;
      }
      setData(res.data);
    }).catch(() => setNotFound(true));
  };

  useEffect(() => {
    setData(null);
    setNotFound(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const submitReview = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/companies/${slug}/reviews`, { rating, text });
      toast.success("Thanks. Your review is live.");
      setReviewed(true);
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="company-not-found">
        <h1 className="font-display text-3xl font-bold text-zinc-950">Company not found</h1>
        <p className="mt-3 text-zinc-600">This page may have moved or the company is no longer listed.</p>
        <Link to="/jobs" className="mt-6 inline-block bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md">Browse jobs</Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-20 animate-pulse" data-testid="company-loading">
        <div className="h-10 bg-zinc-100 rounded w-1/3 mb-4" />
        <div className="h-4 bg-zinc-100 rounded w-1/4" />
      </main>
    );
  }

  const { company, jobs, reviews, rating_avg, review_count } = data;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": company.name,
    "url": `${SITE_URL}/companies/${slug}`,
    ...(company.website ? { "sameAs": company.website } : {}),
    ...(company.description ? { "description": company.description } : {}),
    ...(review_count > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": rating_avg,
        "reviewCount": review_count,
      },
    } : {}),
  };

  return (
    <main className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="company-profile-page">
      <PageGlow />
      <Seo
        title={`${company.name} · Jobs & Reviews | HiredToday`}
        description={company.description ? company.description.slice(0, 155) : `See open roles and reviews for ${company.name} on HiredToday.`}
        path={`/companies/${slug}`}
        image={`${SITE_URL}/api/og/company/${slug}.png`}
        jsonLd={jsonLd}
      />

      <Reveal>
        <div className="glass rounded-lg overflow-hidden">
          {company.brand_color && (
            <div className="h-2 w-full" style={{ backgroundColor: company.brand_color }} data-testid="company-brand-strip" />
          )}
          <div className="p-6 sm:p-10">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            {company.logo && !logoErr ? (
              <img src={fileUrl(company.logo)} alt={`${company.name} logo`} onError={() => setLogoErr(true)} data-testid="company-logo"
                className="shrink-0 h-16 w-16 rounded-md object-cover border border-zinc-200" />
            ) : (
              <div className="shrink-0 h-16 w-16 rounded-md bg-zinc-950 text-white flex items-center justify-center font-display font-black text-2xl">
                {company.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950" data-testid="company-name">{company.name}</h1>
                <span title={company.verified ? "Verified company" : "Not verified yet"} data-testid="company-verified-badge"
                  className="inline-flex shrink-0">
                  <BadgeCheck className={`h-5 w-5 ${company.verified ? "text-blue-600" : "text-zinc-300"}`} />
                </span>
              </div>
              {company.brand_tagline && (
                <p className="mt-2 text-base text-zinc-600 font-medium" data-testid="company-tagline">{company.brand_tagline}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-zinc-500">
                {company.industry && <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" />{company.industry}</span>}
                {company.size && <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" />{company.size} employees</span>}
                {company.address && <span className="inline-flex items-center gap-1.5" data-testid="company-address"><MapPin className="h-4 w-4" />{company.address}</span>}
                {company.phone && <span className="inline-flex items-center gap-1.5" data-testid="company-phone"><Phone className="h-4 w-4" />{company.phone}</span>}
                {company.website && (
                  <a href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                    target="_blank" rel="noopener noreferrer" data-testid="company-website"
                    className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 font-medium">
                    <Globe className="h-4 w-4" /> Website
                  </a>
                )}
              </div>
              {review_count > 0 && (
                <div className="flex items-center gap-2 mt-3" data-testid="company-rating">
                  <Stars value={rating_avg} />
                  <span className="text-sm font-semibold text-zinc-900">{rating_avg}</span>
                  <span className="text-sm text-zinc-500">({review_count} {review_count === 1 ? "review" : "reviews"})</span>
                </div>
              )}
            </div>
          </div>
          {company.description && (
            <p className="mt-6 text-sm sm:text-base text-zinc-600 leading-relaxed whitespace-pre-line" data-testid="company-description">{company.description}</p>
          )}
          {company.photos?.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="company-photos">
              {company.photos.map((p, pi) => (
                <button key={p} type="button" data-testid={`company-photo-${pi}`}
                  onClick={() => setPhotoViewer({ src: fileUrl(p), alt: `${company.name} workplace` })}
                  className="block focus:outline-none">
                  <img src={fileUrl(p)} alt={`${company.name} workplace`} loading="lazy"
                    className="rounded-md object-cover h-32 sm:h-40 w-full border border-zinc-200 hover:opacity-90 transition-opacity duration-200" />
                </button>
              ))}
            </div>
          )}
          {photoViewer && <ImageLightbox src={photoViewer.src} alt={photoViewer.alt} testid="company-photo-viewer" onClose={() => setPhotoViewer(null)} />}
          <div className="mt-6 pt-5 border-t border-zinc-100">
            <ShareButtons path={`/companies/${slug}`} title={`${company.name} · jobs on HiredToday`} testid="company" shareUrl={`${SITE_URL}/api/share/companies/${slug}`} />
          </div>
          </div>
        </div>
      </Reveal>

      <section className="mt-12" data-testid="company-jobs-section">
        <h2 className="font-display text-2xl tracking-tight font-bold text-zinc-950 mb-6">
          Open roles {jobs.length > 0 && <span className="text-zinc-400 font-medium">({jobs.length})</span>}
        </h2>
        {jobs.length === 0 ? (
          <p className="border border-dashed border-zinc-300 rounded-md p-10 text-center text-sm text-zinc-500" data-testid="company-no-jobs">
            No open roles right now. Check back soon.
          </p>
        ) : (
          <div className="grid gap-4">
            {jobs.map((job, i) => (
              <Reveal key={job.id} delay={i * 60}><JobCard job={job} /></Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12" data-testid="company-reviews-section">
        <h2 className="font-display text-2xl tracking-tight font-bold text-zinc-950 mb-6">Reviews</h2>
        {reviews.length === 0 ? (
          <p className="border border-dashed border-zinc-300 rounded-md p-10 text-center text-sm text-zinc-500" data-testid="no-reviews">
            No reviews yet.
          </p>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-zinc-950 text-sm">{r.candidate_name || "Candidate"}</p>
                  <Stars value={r.rating} size="h-3.5 w-3.5" />
                </div>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{r.text}</p>
                <p className="mt-2 text-xs text-zinc-400">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
              </div>
            ))}
          </div>
        )}

        {user && user.role === "candidate" && !reviewed && (
          <form onSubmit={submitReview} className="mt-6 border border-zinc-200 rounded-md p-6 bg-white" data-testid="review-form">
            <p className="font-display font-bold text-zinc-950">Worked with them? Leave a review.</p>
            <div className="mt-4">
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Your rating</label>
              <div className="flex gap-1" data-testid="review-rating-picker">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={`rate-${i}`} onClick={() => setRating(i)} data-testid={`review-star-${i}`}
                    className="p-1 transition-transform duration-150 hover:scale-110" aria-label={`${i} stars`}>
                    <Star className={`h-6 w-6 ${i <= rating ? "fill-yellow-400 text-yellow-400" : "text-zinc-300"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Your review</label>
              <textarea required rows={4} value={text} onChange={(e) => setText(e.target.value)}
                data-testid="review-text" placeholder="Honest and fair. What should candidates know?"
                className="w-full px-4 py-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
            </div>
            <button type="submit" disabled={sending} data-testid="review-submit"
              className="mt-4 h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {sending ? "Posting…" : "Post review"}
            </button>
            <p className="mt-2 text-xs text-zinc-400">One review per company. Reviews are moderated.</p>
          </form>
        )}
        {user === false && (
          <p className="mt-6 text-sm text-zinc-500">
            <Link to={`/login?next=${encodeURIComponent(`/companies/${slug}`)}`} data-testid="review-login-link" className="text-blue-700 font-medium">Log in</Link> with a candidate account to leave a review.
          </p>
        )}
      </section>
    </main>
  );
}
